import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Shield, Download, FileText, ExternalLink, User } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const MyLicenses = () => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasFetched = useRef(false);

  const fetchLicenses = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/licenses/mine`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setLicenses(data.licenses || []);
      }
    } catch (e) {
      console.error('Failed to load licenses:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchLicenses();
    }
  }, [fetchLicenses]);

  const handleDownloadCertificate = async (licenseId) => {
    try {
      const res = await fetch(`${API}/api/licenses/${licenseId}/certificate`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch certificate');
      const cert = await res.json();
      generateCertificateHTML(cert);
    } catch (e) {
      console.error('Failed to download certificate:', e);
    }
  };

  const generateCertificateHTML = (cert) => {
    const isCommercial = cert.license_type === 'commercial' || cert.entitlement_source === 'pro_export';
    const licenseTitle = isCommercial ? 'Commercial License' : 'Personal Use License';
    const introText = isCommercial
      ? 'This certificate confirms that the license holder has been granted a non-exclusive commercial license for the Lumera creation identified below, subject to the Lumera License Terms.'
      : 'This certificate confirms that the license holder has been granted a personal use license for the Lumera creation identified below. This license does not include commercial rights or resale rights.';
    const rightsText = isCommercial ? 'Unlimited eligible end-product sales' : 'Personal use only — no commercial use or resale';
    const tagline = isCommercial ? 'Create it. Customize it. Sell it.' : 'Created just for you.';

    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>Lumera ${licenseTitle} – ${cert.license_id}</title>
<link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap" rel="stylesheet">
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Inter', sans-serif; background: #F7F2EC; color: #4A3525; display: flex; justify-content: center; padding: 40px 20px; }
  .cert { background: #fff; max-width: 680px; width: 100%; border-radius: 16px; padding: 56px 48px; box-shadow: 0 2px 24px rgba(74,53,37,0.06); }
  .logo { font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 600; color: #4A3525; letter-spacing: -0.02em; margin-bottom: 8px; }
  .cert-title { font-family: 'Inter', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 0.18em; text-transform: uppercase; color: ${isCommercial ? '#C4A98D' : '#A0A0A0'}; margin-bottom: 32px; }
  .intro { font-size: 13px; color: #8A7D72; line-height: 1.65; margin-bottom: 36px; }
  .field { margin-bottom: 20px; }
  .field-label { font-size: 10px; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase; color: #A89B8E; margin-bottom: 4px; }
  .field-value { font-size: 15px; font-weight: 500; color: #4A3525; }
  .field-value.mono { font-family: 'SF Mono', 'Fira Code', monospace; letter-spacing: 0.04em; }
  .divider { border: none; border-top: 1px solid #EDE3D6; margin: 28px 0; }
  .status { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; color: #4A3525; background: #EDE3D6; padding: 6px 16px; border-radius: 20px; }
  .status-dot { width: 7px; height: 7px; border-radius: 50%; background: #A89B8E; }
  .tagline { font-family: 'Playfair Display', serif; font-size: 18px; font-style: italic; color: #C4A98D; margin-top: 32px; margin-bottom: 12px; }
  .terms-link { font-size: 11px; color: #A89B8E; }
  .terms-link a { color: #8C7A66; text-decoration: none; }
  .terms-link a:hover { text-decoration: underline; }
  .footer { margin-top: 36px; padding-top: 20px; border-top: 1px solid #EDE3D6; font-size: 10px; color: #A89B8E; }
  @media print { body { background: #fff; padding: 0; } .cert { box-shadow: none; border: 1px solid #EDE3D6; } }
</style></head><body>
<div class="cert">
  <div class="logo">Lumera</div>
  <div class="cert-title">${licenseTitle}</div>
  <p class="intro">${introText}</p>
  <div class="field"><div class="field-label">Licensed to</div><div class="field-value">${cert.licensed_to}</div></div>
  <div class="field"><div class="field-label">Product</div><div class="field-value">${cert.product_title}</div></div>
  <div class="field"><div class="field-label">License ID</div><div class="field-value mono">${cert.license_id}</div></div>
  <div class="field"><div class="field-label">Issued</div><div class="field-value">${new Date(cert.issued_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div></div>
  ${cert.order_id ? `<div class="field"><div class="field-label">Order / Transaction</div><div class="field-value mono">${cert.order_id}</div></div>` : ''}
  <div class="field"><div class="field-label">License Type</div><div class="field-value">${licenseTitle}</div></div>
  <div class="field"><div class="field-label">${isCommercial ? 'Commercial Rights' : 'Usage Rights'}</div><div class="field-value">${rightsText}</div></div>
  <hr class="divider">
  <div class="status"><span class="status-dot"></span>Valid</div>
  <div class="tagline">${tagline}</div>
  <p class="terms-link">Subject to <a href="https://createbylumera.com/license-terms">Lumera License Terms</a> (version ${cert.terms_version})</p>
  <div class="footer">&copy; ${new Date().getFullYear()} Create by Lumera. All rights reserved.</div>
</div></body></html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Lumera-License-${cert.license_id}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div data-testid="licenses-loading" className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-[#DDD4C8] border-t-[#4A3525] rounded-full animate-spin" />
      </div>
    );
  }

  if (licenses.length === 0) {
    return (
      <div data-testid="licenses-empty" className="text-center py-16 px-6">
        <div className="w-16 h-16 mx-auto mb-5 rounded-xl bg-[#EDE3D6] flex items-center justify-center">
          <Shield className="w-7 h-7 text-[#A89B8E]" />
        </div>
        <h3 className="text-lg font-semibold text-[#4A3525] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>No licenses yet</h3>
        <p className="text-sm text-[#8A7D72] max-w-sm mx-auto">
          When you purchase a product or export as a Pro member, your Commercial License will appear here.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="my-licenses" className="space-y-4">
      <div className="flex items-center gap-3 mb-6">
        <Shield className="w-5 h-5 text-[#A89B8E]" />
        <h3 className="text-lg font-semibold text-[#4A3525]" style={{ fontFamily: "'Playfair Display', serif" }}>My Licenses</h3>
        <span className="text-[10px] font-semibold text-[#A89B8E] bg-[#EDE3D6] px-2.5 py-1 rounded-full">{licenses.length}</span>
      </div>

      <div className="space-y-3">
        {licenses.map((lic) => {
          const isCommercial = lic.license_type === 'commercial' || lic.entitlement_source === 'pro_export';
          const isPersonal = lic.license_type === 'personal';
          return (
          <div key={lic.license_id} data-testid={`license-row-${lic.license_id}`}
            className="flex items-center justify-between p-4 bg-white rounded-xl border border-[#DDD4C8]/40 hover:border-[#DDD4C8] transition-all">
            <div className="flex items-center gap-4 min-w-0">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                isCommercial ? 'bg-[#EDE3D6]' : 'bg-stone-100'
              }`}>
                {isCommercial
                  ? <Shield className="w-4 h-4 text-[#8B7B6B]" />
                  : <User className="w-4 h-4 text-stone-400" />
                }
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#4A3525] truncate">{lic.product_title}</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span data-testid={`license-type-${lic.license_id}`} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                    isCommercial
                      ? 'bg-[#F5F0EB] text-[#8B7B6B]'
                      : 'bg-stone-100 text-stone-500'
                  }`}>
                    {isCommercial ? 'Commercial License' : 'Personal Use'}
                  </span>
                  <span className="text-[10px] font-medium text-[#A89B8E]">
                    {lic.entitlement_source === 'pro_export' ? 'Pro Export' : lic.entitlement_source === 'bundle' ? 'Bundle' : 'Purchase'}
                  </span>
                  <span className="text-[10px] text-[#C4A98D]">{new Date(lic.created_at).toLocaleDateString()}</span>
                  <span className="text-[10px] font-mono text-[#A89B8E]">{lic.license_id}</span>
                </div>
              </div>
            </div>
            {isCommercial ? (
              <Button
                data-testid={`download-license-${lic.license_id}`}
                onClick={() => handleDownloadCertificate(lic.license_id)}
                variant="outline"
                size="sm"
                className="border-[#DDD4C8] text-[#8A7D72] hover:bg-[#EDE3D6] text-xs rounded-full px-4 flex-shrink-0">
                <Download className="w-3 h-3 mr-1.5" />Certificate
              </Button>
            ) : (
              <span className="text-[10px] font-sans text-stone-400 flex-shrink-0">Personal use only</span>
            )}
          </div>
          );
        })}
      </div>

      <div className="mt-6 pt-4 border-t border-[#EDE3D6]">
        <a href="/license-terms" className="inline-flex items-center gap-1.5 text-[11px] text-[#A89B8E] hover:text-[#8A7D72] transition-colors">
          <ExternalLink className="w-3 h-3" />View Lumera License Terms
        </a>
      </div>
    </div>
  );
};

export default MyLicenses;
